import { FC } from 'react';

import { Formik } from 'formik';
import { isStrictEqual } from 'remeda';
import { BaseInput, BaseSelect, BaseTextarea, FormBodyContainer } from 'wbs-components-lib';

import { BetCoefficientTypesEnum } from '@shared/constants';

import { BetOutcomeFormProps } from './types';
import { getCoefficientTypeSelectItems } from './utils';
import { betOutcomeFormValidationSchema } from './validationSchema';

export const BetOutcomeForm: FC<BetOutcomeFormProps> = ({
  initialValues,
  onSubmit,
  isSubmitting = false,
  submitButtonCaption = 'Create',
}) => (
  <Formik
    initialValues={initialValues}
    onSubmit={onSubmit}
    validationSchema={betOutcomeFormValidationSchema}
    enableReinitialize
  >
    {({ submitForm, values, handleChange, handleBlur, setFieldValue, errors, touched }) => {
      const {
        name: nameValue,
        description: descriptionValue,
        coefficientType: coefficientTypeValue,
        coefficientValue,
        maxBet: maxBetValue,
      } = values;
      const {
        name: nameError,
        description: descriptionError,
        coefficientType: coefficientTypeError,
        coefficientValue: coefficientValueError,
        maxBet: maxBetError,
      } = errors;
      const {
        name: isNameTouched,
        description: isDescriptionTouched,
        coefficientType: isCoefficientTypeTouched,
        coefficientValue: isCoefficientValueTouched,
        maxBet: isMaxBetTouched,
      } = touched;

      const onSubmit = async () => {
        await submitForm();
      };

      const isNameError = isNameTouched && !!nameError;
      const isDescriptionError = isDescriptionTouched && !!descriptionError;
      const isCoefficientTypeError = isCoefficientTypeTouched && !!coefficientTypeError;
      const isCoefficientValueError = isCoefficientValueTouched && !!coefficientValueError;
      const isMaxBetError = isMaxBetTouched && !!maxBetError;

      const isSubmitButtonDisabled = isStrictEqual(values, initialValues);

      return (
        <FormBodyContainer
          isSubmitting={isSubmitting}
          onSubmit={onSubmit}
          submitButtonCaption={submitButtonCaption}
          isSubmitButtonDisabled={isSubmitButtonDisabled}
        >
          <BaseInput
            helperText={isNameError && nameError}
            label="Enter name"
            id="name"
            name="name"
            caption="Name"
            value={nameValue}
            onBlur={handleBlur}
            onChange={handleChange}
            error={isNameError}
          />
          <BaseTextarea
            helperText={isDescriptionError && descriptionError}
            error={isDescriptionError}
            onBlur={handleBlur}
            inputProps={{ maxLength: 800 }}
            maxRows={6}
            title="Description"
            id="description"
            name="description"
            rows={6}
            label="Enter description"
            value={descriptionValue}
            onChange={handleChange}
          />
          <BaseSelect
            // @ts-ignore
            id="coefficientType"
            name="coefficientType"
            errorText={coefficientTypeError}
            isError={isCoefficientTypeError}
            onBlur={handleBlur}
            items={getCoefficientTypeSelectItems()}
            handleChange={(value: string) => {
              setFieldValue('coefficientType', value);
              setFieldValue('coefficientValue', 1);
            }}
            value={coefficientTypeValue}
            caption="Coefficient Type"
            label="Select type"
          />
          {coefficientTypeValue === BetCoefficientTypesEnum.MANUAL && (
            <BaseInput
              helperText={isCoefficientValueError && coefficientValueError}
              label="Enter coefficient value"
              id="coefficientValue"
              name="coefficientValue"
              caption="Coefficient Value"
              type="number"
              value={coefficientValue}
              onBlur={handleBlur}
              onChange={handleChange}
              error={isCoefficientValueError}
            />
          )}
          <BaseInput
            helperText={isMaxBetError && maxBetError}
            label="Enter max bet"
            id="maxBet"
            name="maxBet"
            caption="Max Bet"
            type="number"
            value={maxBetValue}
            onBlur={handleBlur}
            onChange={handleChange}
            error={isMaxBetError}
          />
        </FormBodyContainer>
      );
    }}
  </Formik>
);
